import { CallCheckerIndicator } from 'components/review-checker-indicators/call-checker-indicator';
import { DuplicateCheckerIndicator } from 'components/review-checker-indicators/duplicate-checker-indicator';
import { IpCheckerIndicator } from 'components/review-checker-indicators/ip-checker-indicator';
import { ProReviewInvitationCheckerIndicator } from 'components/review-checker-indicators/pro-review-invitation-checker-indicator';
import { SolicitationCheckerIndicator } from 'components/review-checker-indicators/solicitation-checker-indicator';
import { useProReviewsCount } from 'hooks/use-pro-reviews-count/use-pro-reviews-count';
import React, { FunctionComponent } from 'react';
import { Link } from 'react-router-dom';
import { Button, Header, Icon, List, Segment } from 'semantic-ui-react';
import { PhoneNumber } from 'utils';
import { ReviewsFilters } from '../hooks/useReviewFilters';
import { Review } from '../types/review.type';
import { getIndividualTimelinePath } from '../utils/getIndividualTimelinePath';

const getLocalityLabel = (review: Review): string | null => {
  if (review.city) {
    const postalCode = Array.isArray(review.city.postalCodes)
      ? review.city.postalCodes[0]
      : undefined;

    return postalCode
      ? `${review.city.name} (${postalCode})`
      : review.city.name;
  }

  if (
    review.addressLocality &&
    review.addressLocality.toUpperCase() !== 'NULL'
  ) {
    return review.addressLocality;
  }

  return null;
};

type ReviewPrivateIndividualSectionProps = {
  review: Review;
  onFilter: (filters: Partial<ReviewsFilters>) => void;
};

export const ReviewPrivateIndividualSection: FunctionComponent<
  ReviewPrivateIndividualSectionProps
> = ({ review, onFilter }) => {
  const presentation = review.referenceView?.view?.proPresentation;
  const hasEmail = review.email.toUpperCase() !== 'NULL';

  // Reviews history of this private individual (all pros / all zones).
  const { count: reviewsCount } = useProReviewsCount(
    hasEmail ? { email: { _eq: review.email } } : { id: { _eq: review.id } },
  );

  const localityLabel = getLocalityLabel(review);

  const reviewPath = hasEmail
    ? `/reviews?${new URLSearchParams({
        privateIndividualEmail: review.email,
        page: '1',
      }).toString()}`
    : null;

  return (
    <Segment.Group>
      <Segment>
        <Header as="h4" style={{ margin: 0 }}>
          Particulier
        </Header>
      </Segment>
      <Segment>
        <List>
          <List.Item>
            <List.Icon name="user" />
            <List.Content>
              <b>{`${review.givenName} ${review.familyName}`}</b>
            </List.Content>
          </List.Item>
          <List.Item>
            <List.Icon name="phone" />
            <List.Content>
              {review.telephone.toUpperCase() !== 'NULL'
                ? PhoneNumber.format(review.telephone)
                : 'Téléphone non renseigné (NULL)'}
            </List.Content>
          </List.Item>
          <List.Item>
            <List.Icon name="map marker alternate" />
            <List.Content>
              {localityLabel ?? 'Lieu non renseigné (NULL)'}
            </List.Content>
          </List.Item>
          <List.Item>
            <List.Icon name="mail" />
            <List.Content>
              {review.email.toUpperCase() !== 'NULL' ? (
                <a
                  href="#"
                  onClick={(): void => {
                    onFilter({ privateIndividualEmail: review.email });
                  }}
                >
                  {review.email}
                </a>
              ) : (
                'Email non renseigné (NULL)'
              )}
            </List.Content>
          </List.Item>
          <List.Item>
            <List.Icon name="desktop" />
            <List.Content>
              <a
                href="#"
                onClick={(): void => {
                  if (review.ip) {
                    onFilter({ ip: review.ip });
                  }
                }}
              >
                {review.ip}
              </a>
            </List.Content>
          </List.Item>
          {reviewPath ? (
            <List.Item>
              <List.Icon name="comment outline" />
              <List.Content>
                <Link to={reviewPath} target="_blank">
                  {reviewsCount ?? '...'} avis déposé
                  {reviewsCount && reviewsCount > 1 ? 's' : ''}
                </Link>
              </List.Content>
            </List.Item>
          ) : null}
        </List>
        <Button
          as={Link}
          to={getIndividualTimelinePath(review)}
          target="_blank"
          basic
          primary
          fluid
          size="tiny"
          style={{ marginTop: '6px' }}
        >
          <Icon name="time" />
          Voir la timeline
        </Button>
        {review.status === 'draft' && presentation ? (
          <List>
            <List.Item>
              <List.Content floated="right">
                <SolicitationCheckerIndicator
                  email={review.email}
                  proPresentationId={presentation.id}
                />
              </List.Content>
              <List.Content>Demande mail</List.Content>
            </List.Item>
            <List.Item>
              <List.Content floated="right">
                <ProReviewInvitationCheckerIndicator
                  email={review.email}
                  proPresentationId={presentation.id}
                  proViewId={presentation.proViews[0]?.id}
                />
              </List.Content>
              <List.Content>Demande d’avis</List.Content>
            </List.Item>
            <List.Item>
              <List.Content floated="right">
                <CallCheckerIndicator
                  telephone={review.telephone}
                  proViewIds={presentation.proViews.map(
                    (proView) => proView.id,
                  )}
                />
              </List.Content>
              <List.Content>Appels</List.Content>
            </List.Item>
            <List.Item>
              <List.Content floated="right">
                <IpCheckerIndicator
                  ip={review.ip}
                  proPresentationId={presentation.id}
                />
              </List.Content>
              <List.Content>IP</List.Content>
            </List.Item>
            <List.Item>
              <List.Content floated="right">
                <DuplicateCheckerIndicator
                  proPresentationId={presentation.id}
                  pi={{
                    email: review.email,
                    givenName: review.givenName,
                    familyName: review.familyName,
                  }}
                />
              </List.Content>
              <List.Content>Doublon</List.Content>
            </List.Item>
          </List>
        ) : null}
      </Segment>
    </Segment.Group>
  );
};
